<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaTemplatesAPI from 'dashboard/api/inbox/metaTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const templates = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const editingTemplateId = ref(null);

const form = reactive({
  name: '',
  language: 'en',
  category: 'utility',
  body: '',
  channel: '',
  submitForApproval: false,
  buttons: [],
});

const categoryOptions = [
  { label: 'utility', value: 'utility' },
  { label: 'marketing', value: 'marketing' },
  { label: 'authentication', value: 'authentication' },
];

const messengerButtonTypeOptions = computed(() => [
  {
    label: t('CONTENT_TEMPLATES.PICKER.TYPES.QUICK_REPLY'),
    value: 'quick_reply',
  },
  {
    label: t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_TYPE_URL'),
    value: 'web_url',
  },
  {
    label: t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_TYPE_POSTBACK'),
    value: 'postback',
  },
]);

const availableChannels = computed(() => {
  if (props.inbox.channel_type === 'Channel::FacebookPage') {
    const channels = [{ label: 'Messenger', value: 'messenger' }];
    if (props.inbox.instagram_id) {
      channels.push({ label: 'Instagram', value: 'instagram' });
    }

    return channels;
  }

  if (props.inbox.channel_type === 'Channel::Instagram') {
    return [{ label: 'Instagram', value: 'instagram' }];
  }

  return [{ label: 'WhatsApp', value: 'whatsapp' }];
});

const activeChannel = ref('');

const isWhatsappChannel = computed(() => form.channel === 'whatsapp');
const isMessengerChannel = computed(() => form.channel === 'messenger');
const showChannelTabs = computed(() => availableChannels.value.length > 1);

const filteredTemplates = computed(() => {
  return templates.value.filter(
    template => template.channel === activeChannel.value
  );
});

const resetForm = () => {
  editingTemplateId.value = null;
  form.name = '';
  form.language = 'en';
  form.category = 'utility';
  form.body = '';
  form.channel = activeChannel.value;
  form.submitForApproval = form.channel === 'whatsapp';
  form.buttons = [];
};

const loadTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await MetaTemplatesAPI.get(props.inbox.id);
    templates.value = data.payload || [];
  } catch {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const submitTemplate = async () => {
  isSaving.value = true;

  const payload = {
    name: form.name,
    language: form.language,
    category: form.category,
    body: form.body,
    channel: form.channel,
    submit_for_approval: form.submitForApproval,
    additional_attributes: isMessengerChannel.value
      ? {
          components: {
            buttons: form.buttons
              .map(button => ({
                type: button.type,
                title: button.title?.trim(),
                url: button.url?.trim(),
                payload: button.payload?.trim(),
              }))
              .filter(button => button.type && button.title)
              .map(button => {
                if (button.type === 'web_url') {
                  return {
                    type: button.type,
                    title: button.title,
                    url: button.url,
                  };
                }

                if (button.type === 'postback') {
                  return {
                    type: button.type,
                    title: button.title,
                    payload: button.payload,
                  };
                }

                return {
                  type: button.type,
                  title: button.title,
                };
              }),
          },
        }
      : {},
  };

  try {
    if (editingTemplateId.value) {
      await MetaTemplatesAPI.update(
        props.inbox.id,
        editingTemplateId.value,
        payload
      );
    } else {
      await MetaTemplatesAPI.create(props.inbox.id, payload);
    }

    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SAVE_SUCCESS'));
    resetForm();
    loadTemplates();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SAVE_ERROR');
    useAlert(errorMessage);
  } finally {
    isSaving.value = false;
  }
};

const editTemplate = template => {
  editingTemplateId.value = template.id;
  form.name = template.name;
  form.language = template.language;
  form.category = template.category;
  form.body = template.body;
  form.channel = template.channel;
  form.submitForApproval = template.submit_for_approval;
  form.buttons =
    template.additional_attributes?.components?.buttons?.map(button => ({
      type: button.type || 'quick_reply',
      title: button.title,
      url: button.url || '',
      payload: button.payload || '',
    })) || [];
};

const addButton = () => {
  form.buttons.push({
    type: 'quick_reply',
    title: '',
    url: '',
    payload: '',
  });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const syncStatus = async template => {
  try {
    await MetaTemplatesAPI.syncStatus(props.inbox.id, template.id);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUS_SYNC_SUCCESS'));
    loadTemplates();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.STATUS_SYNC_ERROR');
    useAlert(errorMessage);
  }
};

watch(
  availableChannels,
  channels => {
    activeChannel.value = channels[0]?.value || '';
    if (!editingTemplateId.value) resetForm();
  },
  { immediate: true }
);

watch(activeChannel, value => {
  if (!editingTemplateId.value) {
    form.channel = value;
    form.submitForApproval = value === 'whatsapp';
  }
});

onMounted(loadTemplates);
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.TITLE')"
    :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SUBTITLE')"
  >
    <div class="grid gap-4">
      <div
        v-if="showChannelTabs"
        class="flex flex-wrap gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-2"
      >
        <button
          v-for="option in availableChannels"
          :key="option.value"
          type="button"
          class="rounded-lg px-3 py-2 text-sm font-medium transition"
          :class="
            activeChannel === option.value
              ? 'bg-n-brand text-white'
              : 'bg-transparent text-n-slate-12 hover:bg-n-alpha-2'
          "
          @click="activeChannel = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="grid gap-3 md:grid-cols-2">
        <Input
          v-model="form.name"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.NAME')"
        />
        <Input
          v-model="form.language"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.LANGUAGE')"
        />
      </div>

      <div class="grid gap-3 md:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CHANNEL') }}
          </label>
          <select
            v-model="form.channel"
            :disabled="!showChannelTabs"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 disabled:opacity-60"
          >
            <option
              v-for="option in availableChannels"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CATEGORY') }}
          </label>
          <select
            v-model="form.category"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12"
          >
            <option
              v-for="option in categoryOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <TextArea
        v-model="form.body"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BODY')"
        auto-height
        resize
        class="[&>div]:!rounded-lg [&>div]:!border [&>div]:!border-n-weak [&>div]:!bg-transparent [&>div]:px-3 [&>div]:py-3"
      />

      <div
        v-if="isMessengerChannel"
        class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTONS') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTONS_HELP') }}
            </p>
          </div>
          <Button
            sm
            color="slate"
            variant="faded"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.ADD_BUTTON')"
            @click="addButton"
          />
        </div>

        <div v-if="form.buttons.length" class="grid gap-3">
          <div
            v-for="(button, index) in form.buttons"
            :key="`messenger-button-${index}`"
            class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
          >
            <div class="grid gap-3 md:grid-cols-2">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_TYPE')
                  }}
                </label>
                <select
                  v-model="button.type"
                  class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12"
                >
                  <option
                    v-for="option in messengerButtonTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <Input
                v-model="button.title"
                :label="
                  $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_TITLE')
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_TITLE_PLACEHOLDER'
                  )
                "
              />
            </div>

            <Input
              v-if="button.type === 'web_url'"
              v-model="button.url"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_URL')"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_URL_PLACEHOLDER'
                )
              "
            />

            <Input
              v-if="button.type === 'postback'"
              v-model="button.payload"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_PAYLOAD')
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.BUTTON_PAYLOAD_PLACEHOLDER'
                )
              "
            />

            <div class="flex justify-end">
              <Button
                color="slate"
                variant="faded"
                :label="
                  $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.REMOVE_BUTTON')
                "
                @click="removeButton(index)"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-sm text-n-slate-11"
      >
        <p class="font-medium text-n-slate-12">
          {{
            isWhatsappChannel
              ? $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.WHATSAPP_HELP')
              : isMessengerChannel
                ? $t(
                    'INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.MESSENGER_BUTTONS_HELP'
                  )
                : $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.DIRECT_HELP')
          }}
        </p>
        <label
          v-if="isWhatsappChannel"
          class="mt-3 flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input v-model="form.submitForApproval" type="checkbox" />
          <span>
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.SUBMIT_FOR_APPROVAL')
            }}
          </span>
        </label>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          v-if="editingTemplateId"
          :label="$t('CONTENT_TEMPLATES.PARSER.GO_BACK_LABEL')"
          color="slate"
          variant="faded"
          @click="resetForm"
        />
        <Button
          :label="
            editingTemplateId
              ? $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')
              : $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.CREATE')
          "
          :is-loading="isSaving"
          :disabled="!form.name || !form.body"
          @click="submitTemplate"
        />
      </div>

      <div class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1">
        <div
          class="flex items-center justify-between border-b border-n-weak px-4 py-3"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.EXISTING') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                availableChannels.find(option => option.value === activeChannel)
                  ?.label || activeChannel
              }}
            </p>
          </div>
          <Button
            sm
            color="slate"
            variant="faded"
            icon="i-lucide-refresh-ccw"
            @click="loadTemplates"
          />
        </div>

        <div v-if="isLoading" class="px-4 py-5 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.LOADING') }}
        </div>
        <div
          v-else-if="!filteredTemplates.length"
          class="px-4 py-5 text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.EMPTY') }}
        </div>
        <div v-else class="divide-y divide-n-weak">
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="flex flex-col gap-3 px-4 py-4"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-n-slate-12">
                  {{ template.name }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{ `${template.status} · ${template.language}` }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <Button
                  sm
                  color="slate"
                  variant="faded"
                  :label="$t('INBOX_MGMT.SETTINGS_POPUP.META_TEMPLATES.EDIT')"
                  @click="editTemplate(template)"
                />
                <Button
                  v-if="template.channel === 'whatsapp'"
                  sm
                  color="slate"
                  variant="faded"
                  :label="
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON'
                    )
                  "
                  @click="syncStatus(template)"
                />
              </div>
            </div>
            <p class="whitespace-pre-wrap text-sm text-n-slate-12">
              {{ template.body }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </SettingsFieldSection>
</template>
